import { AnimatePresence, motion } from 'framer-motion';
import { useRef } from 'react';
import { Box, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Center } from '~/commonComponents';
import CustomSpinner from '~/commonComponents/CustomSpinner';
import { ProfessionalHubRedesign } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useScrollRestore } from '~/hooks/useScrollRestore';
import { CardActionsHeader } from '../CardActionsHeader';
import { EmptyFilteredList } from '../EmptyFilteredList';
import { ProfessionalCard } from '../ProfessionalCard';
import { EmptyRecommendedPros } from './EmptyRecommendedPros';
import { useRecommendedProsList } from './hooks/useRecommendedProsList';
import { ProfessionalCardActions } from './ProfessionalCardActions';
import { ProfessionalCardActionsLegacy } from './ProfessionalCardActionsLegacy';
import { ProfessionalCardDetails } from './ProfessionalCardDetails';
import { ProfessionalCardDetailsLegacy } from './ProfessionalCardDetailsLegacy';

export const RecommendedProsList = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollRestore('RecommendedProsList', scrollRef);

  const isNewCardDetails = useFeatureFlag(ProfessionalHubRedesign);

  const { recommendedProfessionals, isLoading, isEmpty, isFiltered, filter } =
    useRecommendedProsList();

  if (isLoading) {
    return <CustomSpinner isLoading size={24} />;
  }

  if (isEmpty) {
    return (
      <>
        {isFiltered && <EmptyFilteredList />}
        {!isFiltered && <EmptyRecommendedPros />}
      </>
    );
  }

  if (!isNewCardDetails) {
    return (
      <Box width="100%">
        <Center position="relative" textAlign="center">
          <Typography italic color={colors.neutral[500]}>
            The following professionals have shown reliability at other nearby offices. Send an
            invite and fill your shift faster!
          </Typography>
        </Center>

        <div
          ref={scrollRef}
          style={{
            maxWidth: '827px',
            marginTop: 32,
            display: 'flex',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
            height: 600,
            overflowY: 'auto',
          }}
        >
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              padding: '0 50px',
            }}
            layout
          >
            <AnimatePresence>
              <CardActionsHeader actions={['Invite', 'Block']} />
              {recommendedProfessionals?.map((item) => (
                <ProfessionalCard
                  key={item?.id}
                  user={{
                    id: item?.id,
                    fullname: item?.fullname,
                    profileImage: item?.profile_image,
                    professions: item?.professions,
                    profession:
                      item?.professions?.find((prof) => prof === filter) || item?.professions?.[0],
                    isAvailable: item?.isAvailable ?? true,
                    rating:
                      item.rating == null
                        ? undefined
                        : {
                            averageRating: item.rating,
                            professionalSkills: item.averageProfessionalSkills,
                            interpersonalSkills: item.averageInterpersonalSkills,
                            timeManagement: item.averageTimeManagement,
                          },
                  }}
                  Details={ProfessionalCardDetailsLegacy}
                  Actions={ProfessionalCardActionsLegacy}
                  showRemoveIcon={false}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Typography italic color={colors.neutral[500]}>
        The following professionals have shown reliability at other nearby offices. Send an invite
        and fill your shift faster!
      </Typography>

      <div
        ref={scrollRef}
        style={{
          width: '100%',
          marginTop: 16,
          height: 600,
          overflowX: 'hidden',
          overflowY: 'auto',
          paddingRight: 8,
          paddingLeft: 2,
          paddingBottom: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <CardActionsHeader actions={['Block', 'Invite']} isNewDesign />
          {recommendedProfessionals?.map((item) => (
            <ProfessionalCard
              key={item?.id}
              user={{
                id: item?.id,
                fullname: item?.fullname,
                profileImage: item?.profile_image,
                professions: item?.professions,
                profession:
                  item?.professions?.find((prof) => prof === filter) || item?.professions?.[0],
                isAvailable: item?.isAvailable ?? true,
                activityLevel: item?.activity_level,
                yearsOfExperience: item?.years_of_experience,
                completedJobs: item?.completed_jobs,
                badges: item?.badges ?? [],
                rating:
                  item.rating == null
                    ? undefined
                    : {
                        averageRating:
                          typeof item.rating === 'number' ? item.rating : item.rating,
                        professionalSkills: item.averageProfessionalSkills,
                        interpersonalSkills: item.averageInterpersonalSkills,
                        timeManagement: item.averageTimeManagement,
                      },
              }}
              Details={ProfessionalCardDetails}
              Actions={ProfessionalCardActions}
              showRemoveIcon={false}
              isNewDesign
            />
          ))}
        </div>
      </div>
    </Box>
  );
};
